// @ts-nocheck
// src/components/gamification/SuperAdminGameControls.tsx
// SuperAdmin controls for managing gamification settings
// Only accessible by users with superAdmin privileges

import React from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import {
	Settings,
	Play,
	Pause,
	StopCircle,
	Sparkles,
	Calendar,
	Save,
	RefreshCw,
	AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface SuperAdminGameControlsProps {
	userId: Id<'users'>;
	readOnly?: boolean;
}

interface GamificationSetting {
	_id: Id<'gamificationSettings'>;
	_creationTime: number;
	key: string;
	category: string;
	value: unknown;
	description?: string;
	gameStatus?: string;
	currentSeason?: string;
	seasonStartDate?: string;
	seasonEndDate?: string;
	requiresSuperAdmin?: boolean;
	lastUpdatedBy?: Id<'users'>;
	lastUpdated: string;
}

export function SuperAdminGameControls({
	userId,
	readOnly = false,
}: SuperAdminGameControlsProps) {
	const { toast } = useToast();
	const [isUpdating, setIsUpdating] = React.useState(false);
	const [newSeasonName, setNewSeasonName] = React.useState('');

	// Fetch all settings
	const allSettings = useQuery(api.gamificationSettings.getAllSettings);
	const gameStatus = useQuery(api.gamificationSettings.getGameStatus);

	// Mutations
	const initializeSettings = useMutation(
		api.gamificationSettings.initializeSettings
	);
	const updateSetting = useMutation(api.gamificationSettings.updateSetting);
	const updateGameStatus = useMutation(
		api.gamificationSettings.updateGameStatus
	);
	const startNewSeason = useMutation(api.gamificationSettings.startNewSeason);

	// Group settings by category
	const settingsByCategory = React.useMemo(() => {
		if (!allSettings) return {};

		return allSettings.reduce(
			(
				acc: Record<string, GamificationSetting[]>,
				setting: GamificationSetting
			) => {
				if (!acc[setting.category]) {
					acc[setting.category] = [];
				}
				acc[setting.category].push(setting);
				return acc;
			},
			{} as Record<string, GamificationSetting[]>
		);
	}, [allSettings]);

	const handleInitialize = async () => {
		try {
			setIsUpdating(true);
			await initializeSettings({ userId });
			toast({
				title: '✅ Settings Initialized',
				description:
					'Gamification settings have been initialized with defaults',
			});
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to initialize settings',
				variant: 'destructive',
			});
		} finally {
			setIsUpdating(false);
		}
	};

	const handleGameStatusChange = async (newStatus: string) => {
		try {
			setIsUpdating(true);
			await updateGameStatus({ status: newStatus, userId });
			toast({
				title: `🎮 Game ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`,
				description: `The game has been ${newStatus}`,
			});
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to update game status',
				variant: 'destructive',
			});
		} finally {
			setIsUpdating(false);
		}
	};

	const handleStartNewSeason = async () => {
		if (!newSeasonName.trim()) {
			toast({
				title: 'Error',
				description: 'Please enter a season name',
				variant: 'destructive',
			});
			return;
		}

		try {
			setIsUpdating(true);
			await startNewSeason({
				seasonName: newSeasonName,
				userId,
			});
			toast({
				title: '🎉 New Season Started!',
				description: `${newSeasonName} has begun`,
			});
			setNewSeasonName('');
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to start new season',
				variant: 'destructive',
			});
		} finally {
			setIsUpdating(false);
		}
	};

	const handleSettingUpdate = async (key: string, value: unknown) => {
		try {
			setIsUpdating(true);
			await updateSetting({ key, value, userId });
			toast({
				title: '✅ Setting Updated',
				description: `${key} has been updated`,
			});
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to update setting',
				variant: 'destructive',
			});
		} finally {
			setIsUpdating(false);
		}
	};

	// If no settings, show initialize button
	if (!allSettings || allSettings.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className='flex items-center gap-2'>
						<Settings className='h-5 w-5' />
						Social Game Settings
					</CardTitle>
				</CardHeader>
				<CardContent className='text-center py-12'>
					<AlertTriangle className='h-12 w-12 mx-auto mb-4 text-yellow-500' />
					<p className='text-muted-foreground mb-4'>
						Social Game settings have not been initialized
					</p>
					<Button
						onClick={handleInitialize}
						disabled={isUpdating || readOnly}
						className='bg-gradient-to-r from-purple-600 to-pink-600'
					>
						{isUpdating ? (
							<>
								<RefreshCw className='h-4 w-4 mr-2 animate-spin' />
								Initializing...
							</>
						) : (
							<>
								<Sparkles className='h-4 w-4 mr-2' />
								Initialize Settings
							</>
						)}
					</Button>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className='space-y-6'>
			{/* View-only notice for non-superadmins */}
			{readOnly && (
				<Card className='bg-gradient-to-br from-gray-500/5 to-gray-600/5 border-gray-500/20'>
					<CardContent className='p-4'>
						<div className='flex items-center gap-3'>
							<AlertTriangle className='h-5 w-5 text-gray-500' />
							<div className='flex-1'>
								<p className='text-sm font-medium text-gray-400'>
									View-Only Mode
								</p>
								<p className='text-xs text-muted-foreground'>
									You can view game settings but cannot make changes. Only SuperAdmins can modify these settings.
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			)}
			
			{/* Game Status Control */}
			<Card className='bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20'>
				<CardHeader>
					<CardTitle className='flex items-center justify-between'>
						<span className='flex items-center gap-2'>
							<Settings className='h-5 w-5 text-purple-500' />
							<span className='text-xl'>Game Control Center</span>
						</span>
						<Badge
							variant={
								gameStatus?.status === 'active'
									? 'default'
									: 'secondary'
							}
							className={cn(
								'text-sm px-3 py-1',
								gameStatus?.status === 'active' &&
									'bg-gradient-to-r from-green-500 to-emerald-600 border-0'
							)}
						>
							{gameStatus?.status?.toUpperCase() || 'UNKNOWN'}
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className='space-y-4'>
					{/* Current Season Info */}
					{gameStatus?.season && (
						<div className='p-4 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-lg border border-indigo-500/20'>
							<div className='flex items-center justify-between mb-2'>
								<span className='text-sm font-medium text-white'>
									Current Season
								</span>
								<Badge variant='outline' className='bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0'>
									{gameStatus.season}
								</Badge>
							</div>
							{gameStatus.seasonStartDate && (
								<div className='text-xs text-neutral-400'>
									Started:{' '}
									{new Date(
										gameStatus.seasonStartDate
									).toLocaleDateString()}
								</div>
							)}
						</div>
					)}

					{/* Game Control Buttons */}
					<div className='flex gap-3'>
						<Button
							onClick={() => handleGameStatusChange('active')}
							disabled={
								isUpdating || gameStatus?.status === 'active' || readOnly
							}
							variant={
								gameStatus?.status === 'active'
									? 'default'
									: 'outline'
							}
							className={cn(
								'flex-1 transition-all duration-300',
								gameStatus?.status === 'active'
									? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0'
									: 'hover:bg-green-500/20 border-green-500/50'
							)}
						>
							<Play className='h-4 w-4 mr-2' />
							Start Game
						</Button>
						<Button
							onClick={() => handleGameStatusChange('paused')}
							disabled={
								isUpdating || gameStatus?.status === 'paused' || readOnly
							}
							variant={
								gameStatus?.status === 'paused'
									? 'default'
									: 'outline'
							}
							className={cn(
								'flex-1 transition-all duration-300',
								gameStatus?.status === 'paused'
									? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white border-0'
									: 'hover:bg-yellow-500/20 border-yellow-500/50'
							)}
						>
							<Pause className='h-4 w-4 mr-2' />
							Pause Game
						</Button>
						<Button
							onClick={() => handleGameStatusChange('ended')}
							disabled={
								isUpdating || gameStatus?.status === 'ended' || readOnly
							}
							variant={
								gameStatus?.status === 'ended'
									? 'default'
									: 'outline'
							}
							className={cn(
								'flex-1 transition-all duration-300',
								gameStatus?.status === 'ended'
									? 'bg-gradient-to-r from-red-500 to-pink-600 text-white border-0'
									: 'hover:bg-red-500/20 border-red-500/50'
							)}
						>
							<StopCircle className='h-4 w-4 mr-2' />
							End Game
						</Button>
					</div>

					{/* New Season */}
					<div className='pt-4 border-t border-white/10'>
						<Label
							htmlFor='season-name'
							className='text-sm font-medium mb-2 block text-white'
						>
							Start New Season
						</Label>
						<div className='flex gap-2'>
							<Input
								id='season-name'
								placeholder='e.g., Season 2, Summer 2024'
								value={newSeasonName}
								onChange={(e) =>
									setNewSeasonName(e.target.value)
								}
								disabled={isUpdating || readOnly}
								className='bg-black/30 border-white/10 text-white placeholder:text-neutral-500'
							/>
							<Button
								onClick={handleStartNewSeason}
								disabled={isUpdating || !newSeasonName.trim() || readOnly}
								className='bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
							>
								<Calendar className='h-4 w-4 mr-2' />
								Start
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Settings Tabs */}
			<Card className='bg-gradient-to-br from-blue-500/5 to-indigo-500/5 border-blue-500/20'>
				<CardHeader>
					<CardTitle className='text-xl flex items-center gap-2'>
						<Sparkles className='h-5 w-5 text-blue-500' />
						Social Game Configuration
					</CardTitle>
				</CardHeader>
				<CardContent>
					<Tabs defaultValue='scoring' className='w-full'>
						<TabsList className='grid w-full grid-cols-4 bg-gray-900/50'>
							<TabsTrigger 
								value='scoring'
								className='data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white'
							>
								Scoring
							</TabsTrigger>
							<TabsTrigger 
								value='tiers'
								className='data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-600 data-[state=active]:text-white'
							>
								Tiers
							</TabsTrigger>
							<TabsTrigger 
								value='rewards'
								className='data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-600 data-[state=active]:text-white'
							>
								Rewards
							</TabsTrigger>
							<TabsTrigger 
								value='game'
								className='data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-500 data-[state=active]:to-orange-600 data-[state=active]:text-white'
							>
								Game
							</TabsTrigger>
						</TabsList>

						{/* Scoring Settings */}
						<TabsContent value='scoring' className='space-y-4'>
							<div className='space-y-4'>
								{settingsByCategory.scoring?.map(
									(setting: GamificationSetting) => (
										<SettingEditor
											key={setting._id}
											setting={setting}
											onUpdate={handleSettingUpdate}
											isUpdating={isUpdating}
											readOnly={readOnly}
										/>
									)
								)}
							</div>
						</TabsContent>

						{/* Tier Settings */}
						<TabsContent value='tiers' className='space-y-4'>
							<div className='space-y-4'>
								{settingsByCategory.tiers?.map(
									(setting: GamificationSetting) => (
										<SettingEditor
											key={setting._id}
											setting={setting}
											onUpdate={handleSettingUpdate}
											isUpdating={isUpdating}
											readOnly={readOnly}
										/>
									)
								) || (
									<p className='text-gray-500'>
										No tier settings available
									</p>
								)}
							</div>
						</TabsContent>

						{/* Reward Settings */}
						<TabsContent value='rewards' className='space-y-4'>
							<div className='space-y-4'>
								{settingsByCategory.rewards?.map(
									(setting: GamificationSetting) => (
										<SettingEditor
											key={setting._id}
											setting={setting}
											onUpdate={handleSettingUpdate}
											isUpdating={isUpdating}
											readOnly={readOnly}
										/>
									)
								) || (
									<p className='text-gray-500'>
										No reward settings available
									</p>
								)}
							</div>
						</TabsContent>

						{/* Game Settings */}
						<TabsContent value='game' className='space-y-4'>
							<div className='space-y-4'>
								{settingsByCategory.game?.map(
									(setting: GamificationSetting) => (
										<SettingEditor
											key={setting._id}
											setting={setting}
											onUpdate={handleSettingUpdate}
											isUpdating={isUpdating}
											readOnly={readOnly}
										/>
									)
								) || (
									<p className='text-gray-500'>
										No game settings available
									</p>
								)}
							</div>
						</TabsContent>
					</Tabs>
				</CardContent>
			</Card>
		</div>
	);
}

// Setting Editor Component
interface SettingEditorProps {
	setting: GamificationSetting;
	onUpdate: (key: string, value: unknown) => Promise<void>;
	isUpdating: boolean;
	readOnly?: boolean;
}

function SettingEditor({ setting, onUpdate, isUpdating, readOnly = false }: SettingEditorProps) {
	const [editValue, setEditValue] = React.useState(
		typeof setting.value === 'object'
			? JSON.stringify(setting.value, null, 2)
			: String(setting.value || '')
	);
	const [isEditing, setIsEditing] = React.useState(false);

	const handleSave = async () => {
		try {
			const newValue =
				typeof setting.value === 'object'
					? JSON.parse(editValue || '{}')
					: editValue;

			await onUpdate(setting.key, newValue);
			setIsEditing(false);
		} catch (error) {
			// Handle JSON parse error
			console.error('Invalid JSON:', error);
		}
	};

	return (
		<div className='p-4 border border-white/10 rounded-lg space-y-2 bg-gradient-to-br from-gray-900/50 to-gray-800/50 hover:from-gray-900/70 hover:to-gray-800/70 transition-all duration-300'>
			<div className='flex items-center justify-between'>
				<div>
					<h4 className='font-medium text-white'>{setting.key}</h4>
					<p className='text-sm text-neutral-400'>
						{setting.description}
					</p>
				</div>
				<Button
					size='sm'
					variant={isEditing ? 'default' : 'outline'}
					onClick={() =>
						isEditing ? handleSave() : setIsEditing(true)
					}
					disabled={isUpdating || readOnly}
					className={!readOnly && !isEditing ? 'hover:bg-blue-500/20 border-blue-500/50' : ''}
				>
					{isEditing ? (
						<>
							<Save className='h-3 w-3 mr-1' />
							Save
						</>
					) : (
						readOnly ? 'View Only' : 'Edit'
					)}
				</Button>
			</div>

			{isEditing ? (
				<div className='mt-2'>
					{typeof setting.value === 'object' ? (
						<textarea
							className='w-full p-2 border rounded-md font-mono text-sm'
							rows={8}
							value={editValue}
							onChange={(e) => setEditValue(e.target.value)}
						/>
					) : (
						<Input
							value={editValue}
							onChange={(e) => setEditValue(e.target.value)}
							className='font-mono'
						/>
					)}
				</div>
			) : (
				<div className='mt-2 p-3 bg-black/30 rounded-md font-mono text-sm border border-white/5'>
					{typeof setting.value === 'object' ? (
						<pre className='text-blue-400'>
							{JSON.stringify(setting.value || {}, null, 2)}
						</pre>
					) : (
						<span className='text-green-400'>{String(setting.value || '')}</span>
					)}
				</div>
			)}
		</div>
	);
}
